import {useEffect, useRef, useState} from 'react';
import {useSearchParams, useNavigate} from 'react-router-dom';
import {useCapturePaypalPayment} from '../hooks';
import {BookingView} from '../types';
import {BookingData} from '../types';
import {useDispatch} from 'react-redux';
import {loadAuth} from '$redux/authSlice';
import LocalStorage from '$utils/localStore';
import Loading from '~/components/Loading';
import CircularProgress from '@mui/material/CircularProgress';
import {ProcessCardPaymentResponse} from '$api/bookingFlow';
import {RoutePath} from '~/constants/routes';

interface PayPalReturnProps {
  setActiveView: (view: BookingView) => void;
  setBookingChoices: (choices: BookingData) => void;
}

export default function PayPalReturn({
  setActiveView,
  setBookingChoices,
}: PayPalReturnProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const isLinkView = searchParams.get('is_link_view') === 'true';
  const hasCaptured = useRef(false);
  const [isAuthLoaded, setIsAuthLoaded] = useState(false);
  const dispatch = useDispatch();

  const {capturePaypal, isCapturing} = useCapturePaypalPayment(view => {
    if (!isLinkView) {
      if (view === BookingView.AppSummaryView) {
        setActiveView(BookingView.AppSummaryView);
        navigate('/booking', {replace: true});
      } else {
        setActiveView(BookingView.PaymentFailureView);
        navigate('/booking', {replace: true});
      }
      LocalStorage.removeItem(LocalStorage.BOOKING_CHOICES);
      LocalStorage.removeItem(LocalStorage.ACTIVE_VIEW);
    }
  });

  useEffect(() => {
    const authString = LocalStorage.getItem(LocalStorage.AUTH_USER_DATA);
    if (authString) {
      const authObj = JSON.parse(authString);
      const authData = authObj.data?.token
        ? {
            token: authObj.data.token,
            refreshToken: authObj.data.refresh_token,
            user: authObj.data.patient,
          }
        : authObj;

      dispatch(loadAuth(authData));
    }
    setIsAuthLoaded(true);
  }, [dispatch]);

  useEffect(() => {
    if (!isAuthLoaded) return;

    const savedChoices = LocalStorage.getItem(LocalStorage.BOOKING_CHOICES);
    if (savedChoices) {
      setBookingChoices(JSON.parse(savedChoices));
    }

    if (token && !hasCaptured.current) {
      hasCaptured.current = true;
      capturePaypal(token, isLinkView).then(
        (response: ProcessCardPaymentResponse) => {
          if (response?.success && isLinkView) {
            navigate(RoutePath.paymentSuccessRoute, {
              state: {
                paymentSuccessData: response.data,
              },
              replace: true,
            });
          }
        }
      );
    }
  }, [
    token,
    capturePaypal,
    setBookingChoices,
    isAuthLoaded,
    isLinkView,
    navigate,
  ]);

  if (!isAuthLoaded) {
    return <Loading />;
  }

  return (
    <div
      style={{
        padding: 60,
        textAlign: 'center',
        minHeight: 300,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 16,
      }}
    >
      <CircularProgress color="primary" size={64} />
      <h2 style={{margin: '32px 0 8px', color: '#4B2994', fontWeight: 700}}>
        {isCapturing
          ? 'Finalizing your payment...'
          : 'Processing your payment...'}
      </h2>
      <p style={{color: '#555', fontSize: 18, maxWidth: 420, margin: '0 auto'}}>
        Please wait while we securely process your payment.
        <br />
        You will be redirected to your booking summary shortly.
        <br />
        <span role="img" aria-label="secure">
          🔒
        </span>
      </p>
    </div>
  );
}
